/**
 * Vorlage „Abgießen“.
 *
 * Der Topf wird vom Herd genommen und über ein Sieb im Spülbecken gekippt.
 * Das Wasser läuft ab, Nudeln oder Kartoffeln bleiben im Sieb, es dampft.
 */
import { ease, fract, rotatePoint, segment } from '../geometry.ts';
import type { Point } from '../geometry.ts';
import { CANVAS, STROKE } from '../palette.ts';
import { path } from '../paths.ts';
import { circle, compact, faded, group, line, rect, shape, styles } from '../scene.ts';
import type { MaybeNode, SceneNode } from '../scene.ts';
import { steam } from '../parts/effects.ts';
import { colander, stream } from '../parts/kitchen.ts';
import { arrow, counter, GROUND_Y } from '../parts/stage.ts';
import { contents, pot } from '../parts/vessels.ts';
import { choice, defineTemplate, textValue } from '../template.ts';

/** Topf in Ruhe. */
const POT = { cx: 64, width: 84, height: 42 } as const;
const POT_RIM = GROUND_Y - POT.height;
const POT_CENTRE: Point = { x: POT.cx, y: POT_RIM + POT.height / 2 };
/** Lage beim Ausgießen: Versatz und Neigung. */
const POUR = { x: 48, y: -44, rotate: 62 } as const;

/** Sieb im Spülbecken. */
const SIEVE = { cx: 178, rim: 118, width: 80, height: 38 } as const;
const SINK = { x: 120, width: 116 } as const;

/** Zeitplan. */
const TIME = {
  lift: [0.06, 0.24],
  flowIn: [0.24, 0.29],
  flowOut: [0.56, 0.61],
  fill: [0.26, 0.6],
  back: [0.62, 0.8],
  reset: [0.86, 1],
} as const;

/** Gargut: Ton und Form der Stücke. */
const ITEMS = { pasta: 'Nudeln', potatoes: 'Kartoffeln' } as const;
type Item = keyof typeof ITEMS;

/** Kleine Stücke des Garguts für den Strahl und den Topf. */
function bit(item: Item, x: number, y: number, turn: number): SceneNode {
  return item === 'pasta'
    ? group(
        [rect(x - 5, y - 1.8, 10, 3.6, 1.8, { fill: 'shade', stroke: 'ink', strokeWidth: STROKE.fine })],
        {
          transform: { rotate: turn, ox: x, oy: y },
        },
      )
    : circle(x, y, 4.2, { fill: 'tint', stroke: 'ink', strokeWidth: STROKE.fine });
}

/** Haufen im Sieb; amount 0 bis 1 bestimmt die Höhe. */
function heap(item: Item, amount: number, opening: string): SceneNode | null {
  if (amount <= 0.01) {
    return null;
  }
  const rx = SIEVE.width / 2 - 4;
  const top = SIEVE.rim - 2 - amount * 14;
  const mound = path()
    .move(SIEVE.cx - rx, SIEVE.rim + 4)
    .curve(SIEVE.cx - rx * 0.7, top, SIEVE.cx + rx * 0.7, top, SIEVE.cx + rx, SIEVE.rim + 4)
    .close()
    .toString();
  const texture: SceneNode[] =
    item === 'pasta'
      ? [-0.5, -0.15, 0.2, 0.5].map((f, index) =>
          shape(
            path()
              .move(SIEVE.cx + rx * f - 7, SIEVE.rim + 2 - index * 1.5)
              .quad(SIEVE.cx + rx * f, top + 8, SIEVE.cx + rx * f + 7, SIEVE.rim + 1)
              .toString(),
            styles.fine('deep'),
          ),
        )
      : [-0.45, -0.1, 0.25, 0.55].map((f, index) =>
          circle(SIEVE.cx + rx * f, top + 9 + (index % 2) * 3, 6, {
            fill: 'tint',
            stroke: 'ink',
            strokeWidth: STROKE.fine,
          }),
        );
  const clip = `${path()
    .move(SIEVE.cx - SIEVE.width / 2, SIEVE.rim)
    .line(SIEVE.cx - SIEVE.width / 2, SIEVE.rim - 40)
    .line(SIEVE.cx + SIEVE.width / 2, SIEVE.rim - 40)
    .line(SIEVE.cx + SIEVE.width / 2, SIEVE.rim)
    .close()
    .toString()}${opening}`;
  return group(
    [
      shape(mound, { fill: item === 'pasta' ? 'shade' : 'paper', stroke: 'ink', strokeWidth: STROKE.detail }),
      group(texture, { clip: mound }),
    ],
    {
      clip,
    },
  );
}

/** Vorlage „Abgießen“. */
export const stoveDrainTemplate = defineTemplate({
  id: 'stove.drain',
  group: 'stove',
  title: 'Abgießen',
  description:
    'Der Topf wird über ein Sieb im Spülbecken gekippt. Das Kochwasser läuft ab, Nudeln oder Kartoffeln bleiben im Sieb zurück.',
  durationSeconds: 6,
  keywords: ['abgießen', 'abgiessen', 'abtropfen', 'durch ein sieb', 'abseihen', 'abschütten', 'sieb'],
  stillTime: 0.42,
  parameters: [choice('item', 'Gargut', ITEMS, 'pasta')],
  inferValues(text) {
    const lower = text.toLowerCase();
    if (/kartoffel|gemüse|klöße|knödel/.test(lower)) return { item: 'potatoes' };
    if (/nudel|pasta|spaghetti|reis/.test(lower)) return { item: 'pasta' };
    return {};
  },
  scene(values, t) {
    const item = textValue(values, 'item', 'pasta') as Item;
    const pose = ease.inOutCubic(segment(t, ...TIME.lift)) - ease.inOutCubic(segment(t, ...TIME.back));
    const flow =
      ease.inOutSine(segment(t, ...TIME.flowIn)) * (1 - ease.inOutSine(segment(t, ...TIME.flowOut)));
    const reset = ease.inOutSine(segment(t, ...TIME.reset));
    const fill = ease.outQuad(segment(t, ...TIME.fill));
    const transform = {
      x: POUR.x * pose,
      y: POUR.y * ease.outQuad(pose),
      rotate: POUR.rotate * pose,
      ox: POT_CENTRE.x,
      oy: POT_CENTRE.y,
    };

    // Inhalt des Topfs: Wasser mit Gargut; beim Ausgießen leert er sich.
    const vessel = pot(POT.cx, POT_RIM, POT.width, POT.height);
    const inPot = Math.min(1, 1 - fill + reset);
    const potContents = faded(inPot, [
      contents(vessel, 0.3, 'tint', [
        bit(item, POT.cx - 18, POT_RIM + 12, 20),
        bit(item, POT.cx + 2, POT_RIM + 16, -30),
        bit(item, POT.cx + 20, POT_RIM + 11, 60),
        bit(item, POT.cx - 4, POT_RIM + 8, 5),
      ]),
    ]);

    // Ausgussecke des gekippten Topfs in Bildkoordinaten.
    const lipLocal = rotatePoint({ x: POT.cx + POT.width / 2, y: POT_RIM }, POT_CENTRE, transform.rotate);
    const lip = { x: lipLocal.x + transform.x, y: lipLocal.y + transform.y };
    const flowPoints: Point[] = [
      lip,
      { x: lip.x + 14, y: lip.y + 12 },
      { x: SIEVE.cx - 8, y: SIEVE.rim - 2 },
    ];
    const bits: MaybeNode[] =
      flow > 0.6
        ? [0, 0.3, 0.6].map((offset, index) => {
            const f = fract(t * 4 + offset);
            const a = flowPoints[f < 0.5 ? 0 : 1] as Point;
            const b = flowPoints[f < 0.5 ? 1 : 2] as Point;
            const local = f < 0.5 ? f * 2 : (f - 0.5) * 2;
            return bit(item, a.x + (b.x - a.x) * local, a.y + (b.y - a.y) * local, index * 50 + f * 90);
          })
        : [];

    const sieve = colander(SIEVE.cx, SIEVE.rim, SIEVE.width, SIEVE.height);
    const steamStrength = Math.max(flow, 0.6 * (1 - segment(t, 0.62, 0.9))) * (t > TIME.flowIn[0] ? 1 : 0);
    return compact([
      ...counter(),
      // Spülbecken in der Arbeitsfläche
      rect(SINK.x, GROUND_Y, SINK.width, CANVAS.height - GROUND_Y, 0, styles.flat('cavity')),
      line(SINK.x, GROUND_Y, SINK.x, CANVAS.height, styles.outline()),
      line(SINK.x + SINK.width, GROUND_Y, SINK.x + SINK.width, CANVAS.height, styles.outline()),
      line(SINK.x - 6, GROUND_Y, CANVAS.width, GROUND_Y, styles.outline()),
      ...sieve.back,
      fill > 0.01 ? faded(1 - reset, [heap(item, fill, sieve.opening)]) : null,
      ...sieve.front,
      group([...vessel.back, potContents, ...vessel.front], { transform }),
      flow > 0.02 ? stream(flowPoints, 9 * flow, t, 'tint') : null,
      ...bits,
      ...steam(SIEVE.cx, SIEVE.rim - 8, t, { count: 3, spread: 18, height: 34, strength: steamStrength }),
    ]);
  },
  hints() {
    return [
      arrow([
        { x: 60, y: 80 },
        { x: 90, y: 46 },
        { x: 130, y: 46 },
      ]),
    ];
  },
});
